import React, { useState, useEffect, useRef, Suspense } from 'react';
import { View, Text, StyleSheet, ScrollView, Animated, ActivityIndicator, Platform } from 'react-native';
import Svg, { G, Path, Circle } from 'react-native-svg';
import { useTheme } from '../../../theme/useTheme';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { fetchInitialData } from '../../../store/slices/adminSlice';
import { fetchKfaCount } from '../../../store/slices/adminSlice';
import { fetchEmployees } from '../../../store/slices/employeeUpdatesSlice';
import { fetchRoleStats } from '../../../store/thunks/adminThunks';
import { selectRoleStats } from '../../../store/slices/adminRoleStatsSlice';
import { setDashboardStats, selectDashboardStats } from '../../../store/slices/userManagementSlice';
import { userManagementApi } from './UserManagement/apiRoute';
import AdminCard, { AdminCardProps } from '../components/AdminCard';
import { apiClient, createApiClient } from '../../../api/apiClient';
import { type Location as AdminLocation } from './LocationManagement/locationTypes';
import reportingConfigurationApi from './ReportingConfiguration/reportingConfigurationApi';
import { fetchRouteStats } from './RouteManagement';
import PeopleHubConfiguration from '../../PeopleHub/PeopleHubConfiguration';
import { CONFIG_METRICS, CONFIG_TABS } from '../../PeopleHub/PeopleHubConfiguration/constants';
//import RouteManagementScreen, { fetchRouteStats } from './RouteManagement';
import DeliveryRPMManagementScreen from './DeliveryRPMManagement';

import FontFamily from '../../../constants/fonts';
import { useToast } from '../../../components/LhToastContainer';

// Lazy-loaded — each admin sub-module (esp. LocationManagement, which now pulls
// in maplibre-gl + ~14k lines of new components) is fetched as its own Metro
// chunk on demand instead of being bundled eagerly with the dashboard cards.
const HomepageConfigScreen = React.lazy(() => import('./HomepageConfigScreen'));
const LocationManagementScreen = React.lazy(() => import('./LocationManagementScreen'));
const LocationDetail = React.lazy(() => import('./LocationManagement/LocationDetail'));
const EditLocationModal = React.lazy(() => import('./LocationManagement/EditLocationModal'));
const ManageEmployeeUpdatesScreen = React.lazy(() => import('../../NewHireOnboarding/pages/ManageEmployeeUpdates'));
const KFADashboard = React.lazy(() => import('./KfaManagement'));
const RoleManagementPage = React.lazy(() => import('./RoleManagement/RoleManagementPage'));
const PerfectTruckScreen = React.lazy(() => import('./PerfectTruckManagement'));
const UserManagementScreen = React.lazy(() => import('./UserManagementScreen'));
const ReportingConfigurationScreen = React.lazy(() => import('./ReportingConfiguration'));
const RouteManagementScreen = React.lazy(() => import('./RouteManagement'));

const AdminScreenFallback: React.FC = () => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  return (
    <View style={[
      { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#EEF2F8' },
      isDark && { backgroundColor: colors.background.page },
    ]}>
      <ActivityIndicator size="large" color={isDark ? colors.brand.primary : '#005CBC'} />
    </View>
  );
};

// Placeholder components for future development
// const HomepageConfigScreen: React.FC<{ onBack: () => void }> = ({ onBack }) => (
//   <View style={styles.placeholderContainer}>
//     <Text onPress={onBack} style={styles.backLink}>← Back</Text>
//     <Text style={styles.placeholderText}>Homepage Configuration - Coming Soon</Text>
//   </View>
// );

// const LocationManagementScreen: React.FC<{ onBack: () => void }> = ({ onBack }) => (
//   <View style={styles.placeholderContainer}>
//     <Text onPress={onBack} style={styles.backLink}>← Back</Text>
//     <Text style={styles.placeholderText}>Location Management - Coming Soon</Text>
//   </View>
// );

type AdminScreen =
  | 'dashboard'
  | 'homepage-config'
  | 'location-management'
  | 'employee-updates'
  | 'key-focus-areas'
  | 'kfa-dashboard'
  | 'roles-management'
  | 'perfect-truck'
  | 'user-management'
  | 'reporting-configuration'
  | 'route-management'
  | 'people-hub-config'
  | 'location-detail-from-route'
  | 'delivery-rpm-management';

interface AdminDashboardProps {
  onBackPress?: () => void;
}

// ─── Icons ────────────────────────────────────────────────────────────────────
interface IconProps {
  color: string;
}

const HomeIcon: React.FC<IconProps> = ({ color }) => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" fill={color} />
  </Svg>
);

const LocationIcon: React.FC<IconProps> = ({ color }) => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
      fill={color}
    />
  </Svg>
);

const EmployeeIcon: React.FC<IconProps> = ({ color }) => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path
      d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
      fill={color}
    />
  </Svg>
);

const KeyFocusIcon: React.FC<IconProps> = ({ color }) => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path
      d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
      fill={color}
    />
  </Svg>
);

const RolesIcon: React.FC<IconProps> = ({ color }) => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
      fill={color}
    />
  </Svg>
);

const TruckIcon: React.FC<IconProps> = ({ color }) => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path
      d="M20 8H17V4H3C1.9 4 1 4.9 1 6V17H3C3 18.66 4.34 20 6 20C7.66 20 9 18.66 9 17H15C15 18.66 16.34 20 18 20C19.66 20 21 18.66 21 17H23V12L20 8ZM6 18.5C5.17 18.5 4.5 17.83 4.5 17C4.5 16.17 5.17 15.5 6 15.5C6.83 15.5 7.5 16.17 7.5 17C7.5 17.83 6.83 18.5 6 18.5ZM19.5 9.5L21.46 12H17V9.5H19.5ZM18 18.5C17.17 18.5 16.5 17.83 16.5 17C16.5 16.17 17.17 15.5 18 15.5C18.83 15.5 19.5 16.17 19.5 17C19.5 17.83 18.83 18.5 18 18.5Z"
      fill={color}
    />
  </Svg>
);

const RouteManagementIcon: React.FC<IconProps> = ({ color }) => (
  <Svg width="24" height="24" viewBox="0 0 200 200" fill="none">
    <G
      fill="none"
      stroke={color}
      strokeWidth="12"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* Bottom-left node */}
      <Circle cx="55" cy="140" r="14" />
      {/* Top-right node */}
      <Circle cx="145" cy="60" r="14" />
      {/* Continuous S-route path */}
      <Path d="M 69 140 L 145 140 A 20 20 0 0 0 145 100 L 55 100 A 20 20 0 0 1 55 60 L 131 60" />
    </G>
  </Svg>
);

const UserManagementIcon: React.FC<IconProps> = ({ color }) => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path
      d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
      fill={color}
    />
    <Path
      d="M19 7c0-1.1-.9-2-2-2s-2 .9-2 2 .9 2 2 2 2-.9 2-2z"
      fill={color}
    />
  </Svg>
);

const ReportingConfigurationIcon: React.FC<IconProps> = ({ color }) => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path
      d="M3 13h4v8H3v-8zm7-5h4v13h-4V8zm7-6h4v19h-4V2z"
      fill={color}
    />
  </Svg>
);

const DeliveryRPMManagementIcon: React.FC<IconProps> = ({ color }) => (
  <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"
      fill={color}
    />
  </Svg>
);

// ─── Tag color presets ────────────────────────────────────────────────────────
const TAG_LIGHT = {
  blue:   { bgColor: '#DAEEFA', textColor: '#01346F' },
  amber:  { bgColor: '#FEF1B9', textColor: '#995211' },
  green:  { bgColor: '#D4EDDA', textColor: '#1A6B32' },
  gray:   { bgColor: '#E9ECEF', textColor: '#3A3A3A' },
};

const TAG_DARK = {
  blue:   { bgColor: 'rgba(61,139,255,0.22)',  textColor: '#99CFFF' },
  amber:  { bgColor: 'rgba(217,150,0,0.22)',   textColor: '#FFD166' },
  green:  { bgColor: 'rgba(56,182,56,0.22)',   textColor: '#8FEBA8' },
  gray:   { bgColor: 'rgba(176,176,176,0.18)', textColor: '#D4D4D4' },
};

// ─── Card definitions ─────────────────────────────────────────────────────────
interface CardDef extends Omit<AdminCardProps, 'icon'> {
  id: AdminScreen;
  renderIcon: (color: string) => React.ReactNode;
}

const CARD_DEFS: CardDef[] = [
  {
    id: 'homepage-config',
    renderIcon: (color) => <HomeIcon color={color} />,
    title: 'Homepage Configuration',
    description: 'Banners, change log, events, training, external links.',
    tags: [], // populated dynamically from Redux
  },
  {
    id: 'location-management',
    renderIcon: (color) => <LocationIcon color={color} />,
    title: 'Location Management',
    description: 'Facilities, SAP sync, site configuration.',
    tags: [], // populated dynamically from Redux
  },
  {
    id: 'employee-updates',
    renderIcon: (color) => <EmployeeIcon color={color} />,
    title: 'Employee Updates',
    description: 'Manage employee records, access levels, and location assignments.',
    tags: [], // populated dynamically from Redux
  },
  {
    id: 'kfa-dashboard',
    renderIcon: (color) => <KeyFocusIcon color={color} />,
    title: 'Key Focus Areas',
    description: 'Manage Key Focus Areas across Observations, Shift Handover, Leadership Walk, SPARC Walk, NHO, and Site Evaluation.',
    tags: [], // populated dynamically from Redux
  },
  {
    id: 'roles-management',
    renderIcon: (color) => <RolesIcon color={color} />,
    title: 'Roles Management',
    description: 'Manage warehouse roles for Observations and Labor Optimization.',
    tags: [], // populated dynamically from Redux
  },
  {
    id: 'perfect-truck',
    renderIcon: (color) => <TruckIcon color={color} />,
    title: 'Perfect Truck',
    description: 'Configure equipment items, photo categories, and route types for Perfect Truck certifications.',
    tags: [],
  },
  {
    id: 'user-management',
    renderIcon: (color) => <UserManagementIcon color={color} />,
    title: 'User Management',
    description: 'View, search, and manage all Navigator user records, roles, locations, and training completion.',
    tags: [], // populated dynamically from Redux
  },
  {
    id: 'reporting-configuration',
    renderIcon: (color) => <ReportingConfigurationIcon color={color} />,
    title: 'Reporting Configuration',
    description: 'Configure reports metadata, tags, KPI assignments, and report visibility in Analytics Hub.',
    tags: [],
  },
  {
    id: 'route-management',
    renderIcon: (color) => <RouteManagementIcon color={color} />,
    title: 'Route Management',
    description: 'Manage route numbers, route types, and active status across all locations.',
    tags: [], // populated dynamically from API
  },
  {
    id: 'people-hub-config',
    iconEl: <UserManagementIcon />,
    title: 'People Hub Configuration',
    description: 'Configure the onboarding ramp, cadence, metrics and targets, focus areas, and escalation for each org and department.',
    tags: [], // derived from the sections and metrics the People Hub Configuration page renders
  },
  {
    id: 'delivery-rpm-management',
    renderIcon: (color) => <DeliveryRPMManagementIcon color={color} />,
    title: 'Delivery RPM Management',
    description: 'Configure haulback reason codes and route roadblock types for the Delivery RPM module.',
    tags: [],
  }
];

const mapRouteSearchLocationToUiLocation = (raw: any): AdminLocation => ({
  id: String(raw?.id ?? raw?.locId ?? ''),
  sap: String(raw?.locSapId ?? ''),
  name: String(raw?.locName ?? raw?.name ?? ''),
  facilityType: raw?.locationFacilityType ?? '',
  type: raw?.locationType ?? '',
  org: raw?.organizationType ?? '',
  tpl: raw?.is3pl === true ? 'Yes' : raw?.is3pl === false ? 'No' : '',
  size: raw?.size ?? '',
  region: raw?.region ?? '',
  market: raw?.market ?? '',
  marketLead: raw?.marketLead ?? '',
  regionLead: raw?.regionLead ?? '',
  coe: raw?.coeResource ?? '',
  coe2: raw?.secondaryCoeResource ?? '',
  status: raw?.status ?? '',
  empCount: Number(raw?.headcount ?? 0),
  isDuo: Boolean(raw?.isDuo),
  executionSystem: raw?.executionSystem ?? '',
  edfSynced: Boolean(raw?.edfSynced ?? false),
  address: raw?.streetAddress ?? '',
  city: raw?.city ?? '',
  state: raw?.stateProvince ?? '',
  zip: raw?.postalCode ?? '',
  latitude: raw?.latitude != null ? String(raw.latitude) : '',
  longitude: raw?.longitude != null ? String(raw.longitude) : '',
  ownership: raw?.ownership ?? '',
  yearBuilt: raw?.yearBuilt != null ? String(raw.yearBuilt) : '',
  warehouseSystem: raw?.warehouseSystem ?? '',
  warehouseStartTime: raw?.warehouseStartTime ?? '',
  labor: raw?.laborModel ?? '',
  union: raw?.unionStatus ?? '',
  supplier: raw?.tplSupplier ?? '',
  totalSqft: raw?.totalBuildingSqft != null ? String(raw.totalBuildingSqft) : '',
  operationalSqft: raw?.operationalSqft != null ? String(raw.operationalSqft) : '',
  pickedVolume: raw?.pickedVolume != null ? String(raw.pickedVolume) : '',
  salesVolume: raw?.salesVolume != null ? String(raw.salesVolume) : '',
  productionVolume: raw?.productionVolume != null ? String(raw.productionVolume) : '',
  docks: raw?.dockCount != null ? String(raw.dockCount) : '',
  docksEng: raw?.docksWithENGControls != null ? String(raw.docksWithENGControls) : '',
  docksNoEng: raw?.docksWithoutENGControls != null ? String(raw.docksWithoutENGControls) : '',
  driveIn: raw?.driveInBays != null ? String(raw.driveInBays) : '',
  bottleLines: raw?.bottleLines != null ? String(raw.bottleLines) : '',
  canLines: raw?.canLines != null ? String(raw.canLines) : '',
  build1: raw?.crossdockBuildSite1 ?? '',
  build2: raw?.crossdockBuildSite2 ?? '',
  bulkSite: raw?.centralizedBulkSite ?? '',
});

// ─── Screen ───────────────────────────────────────────────────────────────────
const AdminDashboard: React.FC<AdminDashboardProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const tagColors = isDark ? TAG_DARK : TAG_LIGHT;
  const iconColor = isDark ? colors.brand.primary : '#005CBC';
  const [currentScreen, setCurrentScreen] = useState<AdminScreen>('dashboard');
  const hasPrefetchedHomepageConfig = React.useRef(false);
  const hasFetchedEmployees = React.useRef(false);
  const apiRef = React.useRef(createApiClient());

  // Location detail view state (for viewing locations from route management)
  const [selectedLocationName, setSelectedLocationName] = useState<string | null>(null);
  const [selectedLocationObj, setSelectedLocationObj] = useState<any>(null);
  const [locationDetailEmployees, setLocationDetailEmployees] = useState<any[]>([]);
  const [isLoadingLocationDetail, setIsLoadingLocationDetail] = useState(false);
  const [showEditLocationModal, setShowEditLocationModal] = useState(false);
  const toast = useToast();

  // Homepage config selectors
  const bannerCount = useSelector((state: RootState) => state.configuration.homepageConfig.banners.length);
  const eventCount = useSelector((state: RootState) => state.configuration.homepageConfig.events.length);
  const linkCount = useSelector((state: RootState) => state.configuration.homepageConfig.externalLinks.length);
  const userRoleName = useSelector((state: RootState) => state.appData?.userInfo?.roleName ?? '');

  // Location counts — fetched directly from the status-counts API
  const [locReviewCount, setLocReviewCount] = useState(0);
  const [locActiveCount, setLocActiveCount] = useState(0);
  const [locDisabledCount, setLocDisabledCount] = useState(0);

  useEffect(() => {
    let mounted = true;
    const fetchStatusCounts = async () => {
      const response = await apiClient(apiRef.current, '/admin/locations/status-counts', 'GET');
      const counts = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];

      if (!mounted) return;

      const activeCount = counts.find((entry: any) => entry?.statusName === 'Active')?.locationCount;
      const disabledCount = counts.find((entry: any) => entry?.statusName === 'Disabled')?.locationCount;
      const reviewCount = counts
        .filter((entry: any) => entry?.statusName === 'Pending Activation' || entry?.statusName === 'Marked for Removal')
        .reduce((sum: number, entry: any) => sum + (Number(entry?.locationCount) || 0), 0);

      setLocReviewCount(Number.isFinite(reviewCount) ? reviewCount : 0);
      setLocActiveCount(Number.isFinite(activeCount) ? activeCount : 0);
      setLocDisabledCount(Number.isFinite(disabledCount) ? disabledCount : 0);
    };
    fetchStatusCounts().catch(() => { /* non-critical — counts stay at 0 */ });

    return () => {
      mounted = false;
    };
  }, []);

  // Employee selectors
  const selectedLocation = useSelector((state: RootState) => (state as any).appData.selectedLocation);
  const employeeList = useSelector((state: RootState) => (state as any).employeeUpdates.employeeList);
  const roleId = useSelector((state: RootState) => (state as any).appData.userInfo?.roleId);
  const moduleAccess = useSelector((state: RootState) => state.appData.moduleAccess);

  // Role stats selectors
  const roleStats = useSelector(selectRoleStats);

  const normalizedUserRoleName = userRoleName.trim().toLowerCase();
  const hasSuperUserAccess = normalizedUserRoleName === 'super user' || roleId === 1 || roleId === 15;
  const canAccessLocationManagement = hasSuperUserAccess || normalizedUserRoleName === 'coe' || normalizedUserRoleName === 'delivery coe';
  const canEditLocations = hasSuperUserAccess;
  const hasEmployeeUpdateAccess = moduleAccess?.some(
    (module) => module.moduleName === 'Employee Update' && module.hasAccess,
  ) ?? false;
  
  // User Management selectors — read from isolated dashboardStats so table
  // page navigation never corrupts the card counts.
  const dashboardStats = useSelector(selectDashboardStats);
  const userMgmtActiveCount = dashboardStats.activeCount;
  const userMgmtNewCount = dashboardStats.newCount;

  // Route Management stats
  const [routeTotalCount, setRouteTotalCount] = useState(0);
  const [routeLocationCount, setRouteLocationCount] = useState(0);

  // Perfect Truck config stats
  const [ptStats, setPtStats] = useState({ equipmentCount: 0, routeTypeCount: 0, photoCategoryCount: 0, loadSequenceCount: 0 });

  // KFA count stats
  const kfaStats = useSelector((state: RootState) => state.configuration.keyFocusAreas.stats);
  const kfaStatsLoading = useSelector((state: RootState) => state.configuration.keyFocusAreas.isLoadingStats);
  const reportingRows = useSelector((state: RootState) => state.reportingConfiguration.rows);
  const reportingActiveCountFromRows = reportingRows.filter((row) => row.status === 'Active').length;
  const reportingDisabledCountFromRows = reportingRows.filter((row) => row.status === 'Disabled').length;
  const [reportingActiveCount, setReportingActiveCount] = useState<number>(reportingActiveCountFromRows);
  const [reportingDisabledCount, setReportingDisabledCount] = useState<number>(reportingDisabledCountFromRows);
  const [reportingCountsLoading, setReportingCountsLoading] = useState<boolean>(true);
  const reportingPulseAnim = useRef(new Animated.Value(1)).current;

  // Skeleton pulse animation for KFA card tags
  const kfaPulseAnim = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    if (kfaStatsLoading) {
      Animated.loop(
        Animated.sequence([
          Animated.timing(kfaPulseAnim, { toValue: 0.3, duration: 600, useNativeDriver: true }),
          Animated.timing(kfaPulseAnim, { toValue: 1, duration: 600, useNativeDriver: true }),
        ]),
      ).start();
    } else {
      kfaPulseAnim.stopAnimation();
      kfaPulseAnim.setValue(1);
    }
  }, [kfaStatsLoading, kfaPulseAnim]);

  const visibleCards = hasSuperUserAccess
    ? CARD_DEFS
    : canAccessLocationManagement
      ? CARD_DEFS.filter(c => c.id === 'location-management' || (c.id === 'employee-updates' && hasEmployeeUpdateAccess))
      : CARD_DEFS.filter(c => c.id === 'employee-updates' && hasEmployeeUpdateAccess);

  const activeCount   = Array.isArray(employeeList) ? employeeList.filter((e: any) => e.status === 'Active').length   : 0;
  const inactiveCount = Array.isArray(employeeList) ? employeeList.filter((e: any) => e.status === 'Inactive').length : 0;

  // Fetch homepage config data
  React.useEffect(() => {
    if (hasPrefetchedHomepageConfig.current) return;
    hasPrefetchedHomepageConfig.current = true;
    dispatch(fetchInitialData());
  }, [dispatch]);

  // Fetch employee data
  React.useEffect(() => {
    if (!selectedLocation?.displayText) return;
    if (hasFetchedEmployees.current) return;
    hasFetchedEmployees.current = true;
    dispatch(fetchEmployees({ location: selectedLocation.displayText }));
  }, [dispatch, selectedLocation]);

  // Fetch role statistics
  useEffect(() => {
    dispatch(fetchRoleStats());
  }, [dispatch]);

  // Load user management card stats — single lightweight API call.
  // Re-fetches every time the dashboard view is shown (on mount and when
  // navigating back from any sub-screen) so stale counts are never shown.
  useEffect(() => {
    if (currentScreen !== 'dashboard') return;
    userManagementApi.getStats()
      .then(stats => dispatch(setDashboardStats(stats)))
      .catch(err => console.error('[AdminDashboard] Error loading user stats:', err));
  }, [dispatch, currentScreen]);

  // Fetch KFA counts
  useEffect(() => {
    dispatch(fetchKfaCount());
  }, [dispatch]);

  // Fetch report counts for the Reporting Configuration card.
  // Use API if available, otherwise fall back to counts derived from Redux rows.
  useEffect(() => {
    let mounted = true;
    setReportingCountsLoading(true);
    reportingConfigurationApi.getCounts()
      .then((counts) => {
        if (!mounted) return;
        setReportingActiveCount(Number.isFinite(counts.enabledCount) ? counts.enabledCount : reportingActiveCountFromRows);
        setReportingDisabledCount(Number.isFinite(counts.disabledCount) ? counts.disabledCount : reportingDisabledCountFromRows);
      })
      .catch((err) => {
        console.error('[AdminDashboard] Failed to fetch report counts:', err);
        // keep derived values from Redux rows
      })
      .finally(() => {
        if (!mounted) return;
        setReportingCountsLoading(false);
      });

    return () => { mounted = false; };
  }, [reportingActiveCountFromRows, reportingDisabledCountFromRows]);

  // Pulse animation for reporting skeleton while counts load
  useEffect(() => {
    if (reportingCountsLoading) {
      Animated.loop(
        Animated.sequence([
          Animated.timing(reportingPulseAnim, { toValue: 0.3, duration: 600, useNativeDriver: true }),
          Animated.timing(reportingPulseAnim, { toValue: 1, duration: 600, useNativeDriver: true }),
        ])
      ).start();
    } else {
      reportingPulseAnim.stopAnimation();
      reportingPulseAnim.setValue(1);
    }
  }, [reportingCountsLoading, reportingPulseAnim]);

  // Fetch Route Management stats
  useEffect(() => {
    if (currentScreen !== 'dashboard') return;
    fetchRouteStats()
      .then(stats => {
        setRouteTotalCount(stats.totalRoutes);
        setRouteLocationCount(stats.locationCount);
      })
      .catch(() => { /* non-critical — counts stay at 0 */ });
  }, [currentScreen]);

  // Fetch Perfect Truck config stats
  useEffect(() => {
    if (currentScreen !== 'dashboard') return;
    const api = createApiClient();
    api.get<{ equipmentCount: number; routeTypeCount: number; photoCategoryCount: number; loadSequenceCount: number }>('/perfect-truck/config/statistics')
      .then(res => setPtStats(res.data))
      .catch(() => { /* non-critical — counts stay at 0 */ });
  }, [currentScreen]);

  if (currentScreen === 'homepage-config') {
    return (
      <Suspense fallback={<AdminScreenFallback />}>
        <HomepageConfigScreen onBack={() => setCurrentScreen('dashboard')} />
      </Suspense>
    );
  }
  if (currentScreen === 'location-management') {
    return (
      <Suspense fallback={<AdminScreenFallback />}>
        <LocationManagementScreen onBack={() => setCurrentScreen('dashboard')} canEditLocations={canEditLocations} />
      </Suspense>
    );
  }
  if (currentScreen === 'employee-updates') {
    return (
      <Suspense fallback={<AdminScreenFallback />}>
        <ManageEmployeeUpdatesScreen onBackPress={() => setCurrentScreen('dashboard')} />
      </Suspense>
    );
  }
  if (currentScreen === 'kfa-dashboard' || currentScreen === 'key-focus-areas') {
    return (
      <Suspense fallback={<AdminScreenFallback />}>
        <KFADashboard onBackPress={() => setCurrentScreen('dashboard')} />
      </Suspense>
    );
  }
  if (currentScreen === 'roles-management') {
    return (
      <Suspense fallback={<AdminScreenFallback />}>
        <RoleManagementPage onBack={() => setCurrentScreen('dashboard')} />
      </Suspense>
    );
  }
  if (currentScreen === 'perfect-truck') {
    return (
      <Suspense fallback={<AdminScreenFallback />}>
        <PerfectTruckScreen onHomePress={() => setCurrentScreen('dashboard')} />
      </Suspense>
    );
  }
  if (currentScreen === 'user-management') {
    return (
      <Suspense fallback={<AdminScreenFallback />}>
        <UserManagementScreen onBack={() => setCurrentScreen('dashboard')} />
      </Suspense>
    );
  }
  if (currentScreen === 'people-hub-config') {
    return <PeopleHubConfiguration onBack={() => setCurrentScreen('dashboard')} />;
  }
  if (currentScreen === 'reporting-configuration') {
    return (
      <Suspense fallback={<AdminScreenFallback />}>
        <ReportingConfigurationScreen onBack={() => setCurrentScreen('dashboard')} />
      </Suspense>
    );
  }
  if (currentScreen === 'delivery-rpm-management') {
    return <DeliveryRPMManagementScreen onBack={() => setCurrentScreen('dashboard')} />;
  }
  if (currentScreen === 'route-management') {
    return (
      <Suspense fallback={<AdminScreenFallback />}>
        <RouteManagementScreen
          onBack={() => setCurrentScreen('dashboard')}
        onViewLocationDetail={(locationName: string) => {
          setSelectedLocationName(locationName);
          setSelectedLocationObj(null);
          setLocationDetailEmployees([]);
          setShowEditLocationModal(false);
          setCurrentScreen('location-detail-from-route');
          // Fetch location details and employees
          setIsLoadingLocationDetail(true);
          apiClient(
            apiRef.current,
            '/admin/locations/search',
            'POST',
            {
              q: locationName,
              page: 0,
              pageSize: 1,
            }
          )
            .then(async (result) => {
              const items = Array.isArray(result)
                ? result
                : Array.isArray(result?.items)
                  ? result.items
                  : Array.isArray(result?.data)
                    ? result.data
                    : Array.isArray(result?.content)
                      ? result.content
                      : [];

              if (items.length === 0) {
                setCurrentScreen('route-management');
                return;
              }

              const location = mapRouteSearchLocationToUiLocation(items[0]);
              setSelectedLocationObj(location);

              // Fetch employees for this location
              try {
                const userResp = await apiClient(
                  apiRef.current,
                  `/common/users/users-by-role-ids?roleIds=4,5,6,7,8,9&location=${encodeURIComponent(locationName)}`,
                  'GET'
                );
                const users = userResp?.users || [];

                // Filter out COE, Super User, and Region Leader/Division
                const coeUsers = users.filter((u: any) =>
                  u.Role === 'COE' || u.Role === 'Super User' || u.Role === 'Region Leader/Division'
                );
                const coeUserEmails = new Set(coeUsers.map((u: any) => u.UserEmail));

                const fieldUsers = users.filter((u: any) =>
                  !coeUserEmails.has(u.UserEmail)
                );

                setLocationDetailEmployees(fieldUsers);
              } catch (error) {
                console.error('Error fetching location employees:', error);
                setLocationDetailEmployees([]);
              }
            })
            .catch((error) => {
              console.error('Error fetching location details:', error);
              setCurrentScreen('route-management');
            })
            .finally(() => {
              setIsLoadingLocationDetail(false);
            });
        }}
      />
      </Suspense>
    );
  }
  if (currentScreen === 'location-detail-from-route') {
    if (isLoadingLocationDetail) {
      return (
        <View style={[
          { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#EEF2F8' },
          isDark && { backgroundColor: colors.background.page },
        ]}>
          <ActivityIndicator size="large" color={isDark ? colors.brand.primary : '#005CBC'} />
          <Text style={[
            { marginTop: 12, fontSize: 14, color: '#666' },
            isDark && { color: colors.text.secondary },
          ]}>Loading location details…</Text>
        </View>
      );
    }
    if (selectedLocationObj) {
      return (
        <Suspense fallback={<AdminScreenFallback />}>
          <LocationDetail
            location={selectedLocationObj}
            employees={locationDetailEmployees}
            onBack={() => setCurrentScreen('dashboard')}
            onBackToList={() => setCurrentScreen('route-management')}
            onEdit={() => setShowEditLocationModal(true)}
          />
          <EditLocationModal
            visible={showEditLocationModal}
            location={selectedLocationObj}
            employees={locationDetailEmployees}
            onClose={() => setShowEditLocationModal(false)}
            onSave={(updatedLoc) => {
              setSelectedLocationObj(updatedLoc);
              setShowEditLocationModal(false);
            }}
            onSuccess={(msg) => toast.showToast({ header: 'Success', subtext: msg, variant: 'success' })}
          />
        </Suspense>
      );
    }
  }

  const isMobileNative = Platform.OS !== 'web';
  const isDarkNative = isDark && isMobileNative;

  return (
    <ScrollView
      style={[styles.container, isDarkNative && { backgroundColor: colors.background.page }]}
      contentContainerStyle={styles.content}
    >
      <Text style={[styles.moduleName, isDarkNative && { color: colors.text.secondary }]}>Configurations</Text>
      <Text style={[styles.moduleHeader, isDarkNative && { color: colors.text.primary }]}>Configuration Module</Text>
      <Text style={[styles.moduleSubHeader, isDarkNative && { color: colors.text.secondary }]}>
        Super User administration
      </Text>

      <View style={styles.cardsRow}>
        {visibleCards.map(({ id, renderIcon, ...cardProps }) => {
          const dynamicTags =
            id === 'homepage-config'
              ? [
                  { label: `${bannerCount} ${bannerCount === 1 ? 'Banner' : 'Banners'}`, ...tagColors.blue },
                  { label: `${eventCount} ${eventCount === 1 ? 'Event' : 'Events'}`, ...tagColors.blue },
                  { label: `${linkCount} ${linkCount === 1 ? 'Link' : 'Links'}`, ...tagColors.blue },
                ]
            : id === 'location-management'
              ? [
                  { label: `${locActiveCount} Active`, ...tagColors.green },
                  { label: `${locDisabledCount} Disabled`, ...tagColors.gray },
                  ...(locReviewCount > 0 ? [{ label: `${locReviewCount} require review`, ...tagColors.amber }] : []),
                ]
            : id === 'employee-updates'
              ? [
                  { label: `${activeCount} Active`,   ...tagColors.green },
                  { label: `${inactiveCount} Inactive`, ...tagColors.gray  },
                ]
            : id === 'roles-management'
              ? [
                  { label: `${roleStats.totalRoles} Roles`, ...tagColors.blue },
                  { label: `${roleStats.enabledRoles} Enabled`, ...tagColors.green },
                  { label: `${roleStats.disabledRoles} Disabled`, ...tagColors.gray },
                ]
            : id === 'user-management'
              ? [
                  { label: `${userMgmtActiveCount} Active`, ...tagColors.green },
                  ...(userMgmtNewCount > 0 ? [{ label: `${userMgmtNewCount} New Users`, ...tagColors.blue }] : []),
                ]
            : id === 'reporting-configuration'
              ? [
                  { label: `${reportingActiveCount} Active`, ...tagColors.green },
                  ...(reportingDisabledCount > 0 ? [{ label: `${reportingDisabledCount} Disabled`, ...tagColors.gray }] : []),
                ]
            : id === 'route-management'
              ? [
                  ...(routeTotalCount > 0 ? [{ label: `${routeTotalCount} Routes`, ...tagColors.blue }] : []),
                  ...(routeLocationCount > 0 ? [{ label: `${routeLocationCount} Locations`, ...tagColors.green }] : []),
                ]
            : id === 'perfect-truck'
              ? [
                  ...(ptStats.equipmentCount > 0 ? [{ label: `${ptStats.equipmentCount} Equipment`, ...tagColors.blue }] : []),
                  ...(ptStats.photoCategoryCount > 0 ? [{ label: `${ptStats.photoCategoryCount} Photos`, ...tagColors.blue }] : []),
                  ...(ptStats.routeTypeCount > 0 ? [{ label: `${ptStats.routeTypeCount} Route Types`, ...tagColors.green }] : []),
                  ...(ptStats.loadSequenceCount > 0 ? [{ label: `${ptStats.loadSequenceCount} Load Sequences`, ...tagColors.amber }] : []),
                ]
            : id === 'kfa-dashboard'
              ? (
                  kfaStatsLoading ? [] : [
                    { label: `${kfaStats.totalCount} KFAs`, ...tagColors.blue },
                    { label: `${kfaStats.enabledCount} Enabled`, ...tagColors.green },
                    { label: `${kfaStats.disabledCount} Disabled`, ...tagColors.gray },
                  ]
                )
            : id === 'people-hub-config'
              ? [
                  { label: `${CONFIG_TABS.length} Sections`, ...tagColors.blue },
                  { label: `${CONFIG_METRICS.length} Metrics`, ...tagColors.blue },
                  { label: 'Super Users', ...tagColors.gray },
                ]
            : id === 'delivery-rpm-management'
              ? []
              : cardProps.tags;
          return (
            <AdminCard
              key={id}
              icon={renderIcon(iconColor)}
              {...cardProps}
              tags={dynamicTags}
              onPress={() => setCurrentScreen(id)}
              kfaSkeletonLoading={(id === 'kfa-dashboard' && kfaStatsLoading) || (id === 'reporting-configuration' && reportingCountsLoading)}
              kfaPulseAnim={id === 'kfa-dashboard' ? kfaPulseAnim : id === 'reporting-configuration' ? reportingPulseAnim : undefined}
            />
          );
        })}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#EEF2F8',
  },
  content: {
    padding: 32,
  },
  moduleName: {
    fontSize: 14,
    color: '#2D2D2D',
    marginBottom: 8,
    fontWeight: '500',
    fontFamily: FontFamily.medium,
  },
  moduleHeader: {
    fontSize: 28,
    fontWeight: '700',
    color: '#2D2D2D',
    marginBottom: 6,
    fontFamily: FontFamily.bold,
    lineHeight: 40,
  },
  moduleSubHeader: {
    fontSize: 14,
    fontWeight: '400',
    color: '#616161',
    marginBottom: 28,
    fontFamily: FontFamily.regular,
  },
  cardsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  placeholderContainer: {
    flex: 1,
    backgroundColor: '#EEF2F8',
    padding: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  placeholderText: {
    fontSize: 16,
    color: '#616161',
    fontFamily: FontFamily.regular,
  },
  backLink: {
    fontSize: 14,
    color: '#005CBC',
    fontWeight: '600',
    fontFamily: FontFamily.bold,
    marginBottom: 16,
  },
});

export default AdminDashboard;
